import React, { useState, useEffect, useRef } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { connect } from 'react-redux'
import * as auth from '../_redux/authRedux'
import * as authCrud from '../_redux/authCrud'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { FormattedMessage } from 'react-intl'
/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/
function Login() {
	const dispatch = useDispatch()
	let { status, auth_token, refresh_token } = useParams()

	// const { intl } = props;
	const [loading, setLoading] = useState(false)
	// A successful login unmounts this component (routes swap to the app), so
	// guard the trailing setLoading(false) to avoid a state-update-on-unmounted
	// warning.
	const isMountedRef = useRef(true)
	useEffect(() => () => {
		isMountedRef.current = false
	}, [])
	const LoginSchema = Yup.object().shape({
		email: Yup.string().email('Invalid email').required('Required Field'),
		password: Yup.string().required('Required Field'),
	})
	const formik = useFormik({
		initialValues: { email: '', password: '' },
		validationSchema: LoginSchema,
		onSubmit: (values) => {
			login_with_email_and_password(values)
		},
	})
	const getInputClasses = (fieldname) => {
		if (formik.touched[fieldname] && formik.errors[fieldname]) {
			return 'is-invalid'
		}

		if (formik.touched[fieldname] && !formik.errors[fieldname]) {
			return 'is-valid'
		}

		return ''
	}

	const loadToState = (authToken, refreshToken, user) => {
		dispatch(auth.actions.login({ authToken, refreshToken, user }))
	}

	useEffect(() => {
		if (status === '1' && auth_token && refresh_token) {
			loadToState(
				decodeURIComponent(auth_token),
				decodeURIComponent(refresh_token)
			)
		}
		// eslint-disable-next-line
	}, [status, auth_token, refresh_token])

	const login_with_email_and_password = async (auth_data) => {
		try {
			setLoading(true)
			const response = await authCrud.login(auth_data)
			if (response.requires_business_selection) {
				throw new Error(
					'Default business is not configured for this account.'
				)
			}
			if (!response.auth_token || !response.refresh_token) {
				throw new Error('Unable to Login')
			}
			const user = await authCrud.getUserByToken(response.auth_token)
			loadToState(response.auth_token, response.refresh_token, user)
		} catch (error) {
			formik.setStatus(
				error.response?.data?.message ||
					error.message ||
					'Unable to login!!! Try again'
			)
		} finally {
			if (isMountedRef.current) setLoading(false)
		}
	}
	return (
		<div className='login-form login-signin' id='kt_login_signin_form'>
			{/* begin::Head */}
			<div className='text-center mb-10 mb-lg-20'>
				<h3 className='font-size-h1'>
					<FormattedMessage id='AUTH.LOGIN.TITLE' />
				</h3>
				<p className='text-muted font-weight-bold'>
					Enter your login details
				</p>
			</div>
			{/* end::Head */}

			{/*begin::Form*/}
			<form
				onSubmit={formik.handleSubmit}
				className='form fv-plugins-bootstrap fv-plugins-framework'
			>
				{formik.status && (
					<div className='mb-10 alert alert-custom alert-light-danger alert-dismissible'>
						<div className='alert-text font-weight-bold'>{formik.status}</div>
					</div>
				)}
				<div className='form-group fv-plugins-icon-container'>
					<input
						placeholder='Email'
						type='email'
						className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
							'email'
						)}`}
						name='email'
						autoComplete='email'
						{...formik.getFieldProps('email')}
					/>
					{formik.touched.email && formik.errors.email && (
						<div className='fv-plugins-message-container'>
							<div className='fv-help-block'>{formik.errors.email}</div>
						</div>
					)}
				</div>
				<div className='form-group fv-plugins-icon-container'>
					<input
						placeholder='Password'
						type='password'
						className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
							'password'
						)}`}
						name='password'
						{...formik.getFieldProps('password')}
					/>
					{formik.touched.password && formik.errors.password ? (
						<div className='fv-plugins-message-container'>
							<div className='fv-help-block'>{formik.errors.password}</div>
						</div>
					) : null}
				</div>
				<div className='form-group d-flex flex-wrap justify-content-between align-items-center'>
					<div className='text-dark-50 text-hover-primary my-3 mr-2'></div>
					<button
						id='kt_login_signin_submit'
						type='submit'
						disabled={loading}
						className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
					>
						<span>Sign In</span>
						{loading && <span className='ml-3 spinner spinner-white'></span>}
					</button>
				</div>
			</form>
			{/*end::Form*/}
		</div>
	)
}

export default connect(null, auth.actions)(Login)
